import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Search, Layers, QrCode, Filter, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import Table, { ActionButton, ActionMenu } from '../../components/common/Table';
import Modal, { ConfirmModal, FormModal } from '../../components/common/Modal';
import ReportForm from '../../components/forms/ReportForm';
import apiService from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const BedsPage = () => {
  const { user } = useAuth();
  const [beds, setBeds] = useState([]);
  const [farms, setFarms] = useState([]);
  const [plots, setPlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [farmsLoading, setFarmsLoading] = useState(false);
  const [plotsLoading, setPlotsLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const [selectedFarm, setSelectedFarm] = useState('');
  const [selectedPlot, setSelectedPlot] = useState('');

  const [filters, setFilters] = useState({
    crop: '',
    seed: '',
    source: '',
    page: 1,
    limit: 20,
  });

  // Modal states
  const [selectedBed, setSelectedBed] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Filter collapse state
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(true);

  // QR Code generation state
  const [qrCodeGenerating, setQrCodeGenerating] = useState({});

  // Report modal data
  const [reportBedData, setReportBedData] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    plot_id: '',
    bed_number: '',
    size: '',
    crop: '',
    date: '',
    seed: '',
    source: '',
  });

  const columns = [
    {
      key: 'bed_number',
      title: 'Bed #',
      sortable: true,
      width: '100px',
      render: (bed) => (
        <div className="font-medium text-secondary-900">
          {bed.bed_number || 'N/A'}
        </div>
      ),
    },
    {
      key: 'crop',
      title: 'Crop',
      sortable: true,
      render: (bed) => (
        <div className="flex items-center">
          <div className="bg-green-100 p-2 rounded-lg mr-3">
            <Layers className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <div className="font-medium text-secondary-900">{bed.crop || 'Unknown Crop'}</div>
            <div className="text-sm text-secondary-500">Size: {bed.size || 'N/A'} m²</div>
          </div>
        </div>
      ),
    },
    {
      key: 'seed',
      title: 'Seed',
      sortable: true,
      className: 'hidden md:table-cell',
      render: (bed) => (
        <div>
          <div className="font-medium text-secondary-900">{bed.seed || 'Not specified'}</div>
          <div className="text-sm text-secondary-500">{bed.source || 'Unknown source'}</div>
        </div>
      ),
    },
    {
      key: 'date',
      title: 'Planting Date',
      sortable: true,
      className: 'hidden lg:table-cell',
      render: (bed) => bed.date ? new Date(bed.date).toLocaleDateString() : 'Not set',
    },
    {
      key: 'qr_code',
      title: 'QR Code',
      className: 'hidden lg:table-cell',
      render: (bed) => (
        bed.qr_code ? (
          <div className="flex items-center">
            <QrCode className="w-4 h-4 text-green-600 mr-1" />
            <a
              href={bed.qr_code}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 text-sm"
            >
              View QR
            </a>
          </div>
        ) : (
          <button
            onClick={() => handleGenerateQRCode(bed.id)}
            disabled={qrCodeGenerating[bed.id]}
            className="btn btn-sm btn-secondary text-xs px-2 py-1 disabled:opacity-50"
          >
            {qrCodeGenerating[bed.id] ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600 mr-1"></div>
                Generating...
              </>
            ) : (
              <>
                <QrCode className="w-3 h-3 mr-1" />
                Generate QR
              </>
            )}
          </button>
        )
      ),
    },
    {
      key: 'created_at',
      title: 'Created',
      sortable: true,
      className: 'hidden lg:table-cell',
      render: (bed) => new Date(bed.created_at).toLocaleDateString(),
    },
    {
      key: 'actions',
      title: 'Actions',
      width: '120px',
      render: (bed) => (
        <div className="flex space-x-2">
          <ActionButton
            icon={Eye}
            label="View Details"
            onClick={() => handleViewBed(bed)}
          />
          <ActionMenu
            item={bed}
            actions={[
              {
                key: 'report',
                label: 'Submit Report',
                icon: AlertTriangle,
                variant: 'warning',
                onClick: handleReportBed,
              },
              {
                key: 'edit',
                label: 'Edit Bed',
                icon: Edit,
                onClick: handleEditBed,
              },
              {
                key: 'delete',
                label: 'Delete Bed',
                icon: Trash2,
                variant: 'danger',
                onClick: handleDeleteBed,
              },
            ]}
          />
        </div>
      ),
    },
  ];

  useEffect(() => {
    loadFarms();
  }, []);

  useEffect(() => {
    if (selectedFarm) {
      loadPlots();
      setSelectedPlot('');
      setBeds([]);
    } else {
      setPlots([]);
      setSelectedPlot('');
      setBeds([]);
    }
  }, [selectedFarm]);

  useEffect(() => {
    if (selectedPlot) {
      loadBeds();
    } else {
      setBeds([]);
    }
  }, [selectedPlot, filters, sortConfig]);

  const loadFarms = async () => {
    try {
      setFarmsLoading(true);
      const response = await apiService.getFarms();

      if (response.success) {
        const farmsData = response.data.farms || [];
        setFarms(farmsData);

        // Auto-select first farm if available and no farm is currently selected
        if (farmsData.length > 0 && !selectedFarm) {
          setSelectedFarm(farmsData[0].id);
        }
      } else {
        toast.error(response.message || 'Failed to load farms');
      }
    } catch (error) {
      console.error('Error loading farms:', error);
      toast.error('Failed to load farms');
    } finally {
      setFarmsLoading(false);
    }
  };

  const loadPlots = async () => {
    if (!selectedFarm) return;

    try {
      setPlotsLoading(true);
      const response = await apiService.getPlots({ farm_id: selectedFarm });

      if (response.success) {
        const plotsData = response.data.plots || [];
        setPlots(plotsData);

        // Auto-select first plot if available and no plot is currently selected
        if (plotsData.length > 0 && !selectedPlot) {
          setSelectedPlot(plotsData[0].id);
        }
      } else {
        toast.error(response.message || 'Failed to load plots');
      }
    } catch (error) {
      console.error('Error loading plots:', error);
      toast.error('Failed to load plots');
    } finally {
      setPlotsLoading(false);
    }
  };

  const loadBeds = async () => {
    if (!selectedPlot) return;

    try {
      setLoading(true);
      const params = {
        plot_id: selectedPlot,
        ...filters,
        sort_by: sortConfig.key,
        sort_direction: sortConfig.direction,
      };

      const response = await apiService.getBeds(params);

      if (response.success) {
        setBeds(response.data.beds || []);
        setPagination(response.data.pagination);
      } else {
        toast.error(response.message || 'Failed to load beds');
      }
    } catch (error) {
      console.error('Error loading beds:', error);
      toast.error('Failed to load beds');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleViewBed = (bed) => {
    setSelectedBed(bed);
    setShowViewModal(true);
  };

  const handleEditBed = (bed) => {
    setSelectedBed(bed);
    setFormData({
      plot_id: bed.plot_id || '',
      bed_number: bed.bed_number || '',
      size: bed.size ? bed.size.toString() : '',
      crop: bed.crop || '',
      date: bed.date ? bed.date.split('T')[0] : '',
      seed: bed.seed || '',
      source: bed.source || '',
    });
    setShowEditModal(true);
  };

  const handleDeleteBed = (bed) => {
    setSelectedBed(bed);
    setShowDeleteModal(true);
  };

  const handleReportBed = async (bed) => {
    try {
      // We need to get the full bed data including farm and plot info
      // First get the bed details
      const bedResponse = await apiService.getBed(bed.id);
      if (!bedResponse.success) {
        toast.error('Failed to load bed details');
        return;
      }

      // Get the farm data
      const farmResponse = await apiService.getFarm(selectedFarm);
      if (!farmResponse.success) {
        toast.error('Failed to load farm details');
        return;
      }

      // Get the plot data
      const plotResponse = await apiService.getPlot(bed.plot_id);
      if (!plotResponse.success) {
        toast.error('Failed to load plot details');
        return;
      }

      // Structure the data as expected by ReportForm
      const bedData = {
        bed: bedResponse.data,
        farm: farmResponse.data,
        plot: plotResponse.data
      };

      setReportBedData(bedData);
      setShowReportModal(true);
    } catch (error) {
      console.error('Error loading bed data for report:', error);
      toast.error('Failed to load bed details for reporting');
    }
  };

  const handleReportSuccess = () => {
    setShowReportModal(false);
    setReportBedData(null);
    toast.success('Report submitted successfully');
  };

  const handleCreateBed = () => {
    if (!selectedPlot) {
      toast.error('Please select a plot first');
      return;
    }

    setSelectedBed(null);
    setFormData({
      plot_id: selectedPlot,
      bed_number: '',
      size: '',
      crop: '',
      date: '',
      seed: '',
      source: '',
    });
    setShowCreateModal(true);
  };

  const handleFormSubmit = async () => {
    let processedFormData = null;

    try {
      setFormLoading(true);

      // Validate required fields
      if (!formData.bed_number || !formData.crop || !formData.size) {
        toast.error('Please fill in all required fields');
        setFormLoading(false);
        return;
      }

      // Validate and convert size to number
      const parsedSize = parseFloat(formData.size);
      if (isNaN(parsedSize) || parsedSize < 0) {
        toast.error('Please enter a valid size value');
        setFormLoading(false);
        return;
      }

      // Format date to ISO string if provided
      let formattedDate = null;
      if (formData.date) {
        const dateObj = new Date(formData.date);
        if (!isNaN(dateObj.getTime())) {
          formattedDate = dateObj.toISOString();
        }
      }

      processedFormData = {
        plot_id: formData.plot_id,
        bed_number: formData.bed_number.trim(),
        crop: formData.crop,
        size: parsedSize,
        seed: formData.seed || null,
        source: formData.source || null,
        date: formattedDate
      };

      let response;
      if (selectedBed) {
        response = await apiService.updateBed(selectedBed.id, processedFormData);
      } else {
        response = await apiService.createBed(processedFormData);
      }

      if (response.success) {
        toast.success(selectedBed ? 'Bed updated successfully' : 'Bed created successfully');
        setShowEditModal(false);
        setShowCreateModal(false);
        loadBeds();
      } else {
        toast.error(response.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      console.error('Form data being sent:', processedFormData);

      // Try to extract more specific error message
      const errorMessage = error.response?.data?.message ||
        error.message ||
        'Operation failed. Please check console for details.';
      toast.error(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBed) return;

    try {
      const response = await apiService.deleteBed(selectedBed.id);

      if (response.success) {
        toast.success('Bed deleted successfully');
        setShowDeleteModal(false);
        loadBeds();
      } else {
        toast.error(response.message || 'Failed to delete bed');
      }
    } catch (error) {
      console.error('Error deleting bed:', error);
      toast.error('Failed to delete bed');
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'size') {
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setFormData(prev => ({ ...prev, [field]: value }));
      }
      return;
    }

    if (field === 'bed_number') {
      if (value === '' || /^[a-zA-Z0-9]+$/.test(value)) {
        setFormData(prev => ({ ...prev, [field]: value }));
      }
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerateQRCode = async (bedId) => {
    try {
      setQrCodeGenerating(prev => ({ ...prev, [bedId]: true }));

      const response = await apiService.generateBedQRCode(bedId);

      if (response.success) {
        toast.success('QR code generated successfully');
        loadBeds(); // Reload to get the updated bed with QR code
      } else {
        toast.error(response.message || 'Failed to generate QR code');
      }
    } catch (error) {
      console.error('QR code generation error:', error);
      toast.error(error.message || 'Failed to generate QR code');
    } finally {
      setQrCodeGenerating(prev => ({ ...prev, [bedId]: false }));
    }
  };

  const renderFormFields = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          Bed Number *
        </label>
        <input
          type="text"
          value={formData.bed_number}
          onChange={(e) => handleInputChange('bed_number', e.target.value)}
          className="form-input"
          placeholder="Enter bed number (e.g., A1, B2, 3)"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          Size (m²) *
        </label>
        <input
          type="text"
          inputMode="decimal"
          pattern="[0-9]*\.?[0-9]*"
          value={formData.size}
          onChange={(e) => handleInputChange('size', e.target.value)}
          className="form-input"
          placeholder="Enter bed size in square meters"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          Crop *
        </label>
        <input
          type="text"
          value={formData.crop}
          onChange={(e) => handleInputChange('crop', e.target.value)}
          className="form-input"
          placeholder="Enter crop name"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          Planting Date
        </label>
        <input
          type="date"
          value={formData.date}
          onChange={(e) => handleInputChange('date', e.target.value)}
          className="form-input"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          Seed Variety
        </label>
        <input
          type="text"
          value={formData.seed}
          onChange={(e) => handleInputChange('seed', e.target.value)}
          className="form-input"
          placeholder="Enter seed variety"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          Seed Source
        </label>
        <input
          type="text"
          value={formData.source}
          onChange={(e) => handleInputChange('source', e.target.value)}
          className="form-input"
          placeholder="Enter seed source/supplier"
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-secondary-900">Beds</h1>
          <p className="text-sm md:text-base text-secondary-600">Manage bed information within plots</p>
        </div>
        {selectedPlot && user && ['admin', 'super_admin', 'farms_owner'].includes(user.role) && (
          <button className="btn btn-primary w-full md:w-auto" onClick={handleCreateBed}>
            <Plus className="w-4 h-4 mr-2" />
            <span className="md:inline">Add Bed</span>
          </button>
        )}
      </div>

      {/* Farm and Plot Selection */}
      <div className="bg-white rounded-lg shadow p-3 md:p-6 mx-2 md:mx-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Select Farm *
            </label>
            <select
              value={selectedFarm}
              onChange={(e) => setSelectedFarm(e.target.value)}
              className="form-input w-full text-sm md:text-base"
              disabled={farmsLoading}
            >
              <option value="">Choose a farm...</option>
              {farms.map((farm) => (
                <option key={farm.id} value={farm.id}>
                  {farm.name} - {farm.country}
                </option>
              ))}
            </select>
            {farmsLoading && (
              <p className="mt-1 text-xs md:text-sm text-secondary-500">Loading farms...</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Select Plot *
            </label>
            <select
              value={selectedPlot}
              onChange={(e) => setSelectedPlot(e.target.value)}
              className="form-input w-full text-sm md:text-base"
              disabled={!selectedFarm || plotsLoading}
            >
              <option value="">Choose a plot...</option>
              {plots.map((plot) => (
                <option key={plot.id} value={plot.id}>
                  {plot.name} ({plot.area} ha)
                </option>
              ))}
            </select>
            {plotsLoading && (
              <p className="mt-1 text-xs md:text-sm text-secondary-500">Loading plots...</p>
            )}
            {!selectedFarm && (
              <p className="mt-1 text-xs md:text-sm text-secondary-500">Please select a farm first</p>
            )}
          </div>
        </div>
      </div>

      {/* Filters - Only show when plot is selected */}
      {selectedPlot && (
        <div className="bg-white rounded-lg shadow mx-2 md:mx-0">
          {/* Filter Header */}
          <div className="flex items-center justify-between p-3 md:p-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 md:w-5 md:h-5 text-secondary-500" />
              <h3 className="text-base md:text-lg font-medium text-secondary-900">Filters</h3>
            </div>
            <button
              onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
              className="flex items-center space-x-1 text-secondary-600 hover:text-secondary-900 transition-colors p-1"
            >
              <span className="text-xs md:text-sm">{isFiltersCollapsed ? 'Show' : 'Hide'}</span>
              {isFiltersCollapsed ? (
                <ChevronDown className="w-3 h-3 md:w-4 md:h-4" />
              ) : (
                <ChevronUp className="w-3 h-3 md:w-4 md:h-4" />
              )}
            </button>
          </div>

          {/* Collapsible Filter Content */}
          {!isFiltersCollapsed && (
            <div className="p-3 md:p-6">
              <div className="space-y-3 md:space-y-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-secondary-400" />
                  <input
                    type="text"
                    placeholder="Filter by crop..."
                    value={filters.crop}
                    onChange={(e) => handleFilterChange('crop', e.target.value)}
                    className="form-input pl-8 md:pl-10 w-full text-sm md:text-base"
                  />
                </div>

                {/* Seed and Source Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Filter by seed..."
                    value={filters.seed}
                    onChange={(e) => handleFilterChange('seed', e.target.value)}
                    className="form-input w-full text-sm md:text-base"
                  />
                  <input
                    type="text"
                    placeholder="Filter by source..."
                    value={filters.source}
                    onChange={(e) => handleFilterChange('source', e.target.value)}
                    className="form-input w-full text-sm md:text-base"
                  />
                </div>

                <div className="flex justify-center md:justify-end">
                  <button
                    onClick={() => setFilters({ crop: '', seed: '', source: '', page: 1, limit: 20 })}
                    className="btn btn-secondary text-sm md:text-base px-4 py-2"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Beds Table - Only show when plot is selected */}
      {selectedPlot ? (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block">
            <Table
              columns={columns}
              data={beds}
              loading={loading}
              pagination={pagination}
              onPageChange={handlePageChange}
              onSort={handleSort}
              sortConfig={sortConfig}
              emptyMessage="No beds found in this plot"
            />
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3 px-2">
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            ) : beds.length > 0 ? (
              beds.map((bed) => (
                <div key={bed.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 w-full max-w-full overflow-hidden">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3 w-full">
                    <div className="flex items-center space-x-2 flex-1 min-w-0 overflow-hidden">
                      <div className="p-1.5 rounded-md flex-shrink-0 bg-green-100 text-green-600">
                        <Layers className="w-3 h-3" />
                      </div>
                      <div className="min-w-0 flex-1 overflow-hidden">
                        <div className="font-medium text-gray-900 text-xs truncate">
                          {bed.bed_number && `#${bed.bed_number} - `}{bed.crop || 'Unknown Crop'}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          Size: {bed.size || 'N/A'} m²
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <ActionButton
                        icon={Eye}
                        label="View Details"
                        onClick={() => handleViewBed(bed)}
                        size="sm"
                        className="p-1"
                      />
                      <ActionMenu
                        item={bed}
                        actions={[
                          {
                            key: 'report',
                            label: 'Submit Report',
                            icon: AlertTriangle,
                            variant: 'warning',
                            onClick: handleReportBed,
                          },
                          ...(user && ['admin', 'super_admin', 'farms_owner'].includes(user.role) ? [
                            {
                              key: 'edit',
                              label: 'Edit Bed',
                              icon: Edit,
                              onClick: handleEditBed,
                            },
                            {
                              key: 'delete',
                              label: 'Delete Bed',
                              icon: Trash2,
                              variant: 'danger',
                              onClick: handleDeleteBed,
                            },
                          ] : [])
                        ]}
                      />
                    </div>
                  </div>

                  {/* Info Grid */}
                  <div className="grid grid-cols-2 gap-2 text-xs w-full">
                    <div className="min-w-0 overflow-hidden">
                      <div className="text-gray-500 text-xs uppercase tracking-wide">Seed</div>
                      <div className="font-medium text-gray-900 text-xs truncate">
                        {bed.seed || 'Not specified'}
                      </div>
                      {bed.source && (
                        <div className="text-xs text-gray-500 truncate">
                          {bed.source}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 overflow-hidden">
                      <div className="text-gray-500 text-xs uppercase tracking-wide">Date</div>
                      <div className="font-medium text-gray-900 text-xs truncate">
                        {bed.date ? new Date(bed.date).toLocaleDateString() : 'Not set'}
                      </div>
                      {bed.qr_code && (
                        <a
                          href={bed.qr_code}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-700"
                        >
                          View QR
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded-lg shadow p-6 text-center mx-2">
                <Layers className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No beds found in this plot</p>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="bg-white rounded-lg shadow p-8 md:p-12 text-center mx-2 md:mx-0">
          <Layers className="w-8 h-8 md:w-12 md:h-12 text-secondary-400 mx-auto mb-3 md:mb-4" />
          <h3 className="text-base md:text-lg font-medium text-secondary-900 mb-2">Select Farm and Plot</h3>
          <p className="text-sm md:text-base text-secondary-600">
            Please select a farm and plot above to view and manage beds
          </p>
        </div>
      )}

      {/* View Bed Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Bed Details"
        size="lg"
      >
        {selectedBed && (
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <Layers className="w-8 h-8 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold">{selectedBed.crop}</h3>
                <p className="text-secondary-600">
                  {selectedBed.bed_number && `Bed #${selectedBed.bed_number} | `}Size: {selectedBed.size} m² | ID: {selectedBed.id}
                </p>
              </div>
              {selectedBed.thumbnail && (
                <div className="flex-shrink-0">
                  <img
                    src={selectedBed.thumbnail}
                    alt={`${selectedBed.crop} bed thumbnail`}
                    className="h-20 w-20 rounded-lg object-contain border-2 border-gray-200"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700">Bed Number</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedBed.bed_number || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Crop</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedBed.crop || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Size</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedBed.size} m²</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Planting Date</label>
                <p className="mt-1 text-sm text-secondary-900">
                  {selectedBed.date ? new Date(selectedBed.date).toLocaleDateString() : 'Not set'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Seed Variety</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedBed.seed || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Seed Source</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedBed.source || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">QR Code</label>
                {selectedBed.qr_code ? (
                  <a
                    href={selectedBed.qr_code}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 text-sm text-primary-600 hover:text-primary-700"
                  >
                    View QR Code
                  </a>
                ) : (
                  <div className="mt-1">
                    <p className="text-sm text-secondary-500 mb-2">No QR code generated</p>
                    <button
                      onClick={() => handleGenerateQRCode(selectedBed.id)}
                      disabled={qrCodeGenerating[selectedBed.id]}
                      className="btn btn-sm btn-primary disabled:opacity-50"
                    >
                      {qrCodeGenerating[selectedBed.id] ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <QrCode className="w-4 h-4 mr-2" />
                          Generate QR Code
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Created</label>
                <p className="mt-1 text-sm text-secondary-900">
                  {new Date(selectedBed.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Last Updated</label>
                <p className="mt-1 text-sm text-secondary-900">
                  {new Date(selectedBed.updated_at).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Bed Modal */}
      <FormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleFormSubmit}
        title="Edit Bed"
        loading={formLoading}
      >
        {renderFormFields()}
      </FormModal>

      {/* Create Bed Modal */}
      <FormModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleFormSubmit}
        title="Create New Bed"
        loading={formLoading}
      >
        {renderFormFields()}
      </FormModal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Bed"
        message={`Are you sure you want to delete this bed with crop "${selectedBed?.crop}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      {/* Report Modal */}
      <Modal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Submit Report"
        size="xl"
      >
        {reportBedData && (
          <ReportForm
            bedData={reportBedData}
            onSuccess={handleReportSuccess}
            onCancel={() => setShowReportModal(false)}
          />
        )}
      </Modal>
    </div>
  );
};

export default BedsPage;